import { Deselect, SelectAll } from "@mui/icons-material";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { CheckboxMui, FramerTooltip } from "@ryde/controls";

import { CoursesCalendarRideProduceTableRow } from "./courses-calendar-ride-produce-table-row";
import { CoursesCalendarRideProduceTableSeatsHead } from "./courses-calendar-ride-produce-table-seats-head";
import { ICoursesCalendarRideProducePreviewTableProps } from "./courses-calendar-ride-produce.models";
import { isCoursesCalendarProduceSlotSkipped } from "./courses-calendar-ride-produce.utils";

export const CoursesCalendarRideProducePreviewTable: FC<
  ICoursesCalendarRideProducePreviewTableProps
> = ({
  slots,
  selectedSlotIds,
  selectedCount,
  onToggleSelected,
  onSelectAllReady,
  onDeselectAllReady,
}) => {
  const { t } = useTranslation();
  const [forProduceOnly, setForProduceOnly] = useState(false);

  const visibleSlots = useMemo(() => {
    if (!forProduceOnly) {
      return slots;
    }
    return slots.filter(slot => !isCoursesCalendarProduceSlotSkipped(slot));
  }, [forProduceOnly, slots]);

  const readyForProduceCount = useMemo(
    () =>
      slots.filter(slot => !isCoursesCalendarProduceSlotSkipped(slot)).length,
    [slots],
  );

  const alreadyProducedCount = useMemo(
    () =>
      slots.filter(slot => isCoursesCalendarProduceSlotSkipped(slot)).length,
    [slots],
  );

  const activeNotSelectedCount = useMemo(
    () =>
      slots.filter(
        slot =>
          !isCoursesCalendarProduceSlotSkipped(slot) &&
          !selectedSlotIds.has(slot.id),
      ).length,
    [selectedSlotIds, slots],
  );

  const toggleForProduceOnly = useEvent(() => {
    setForProduceOnly(prev => !prev);
  });

  return (
    <>
      <div className="cc-ride-produce-toolbar">
        <label className="cc-ride-produce-filter">
          <CheckboxMui
            width={15}
            height={15}
            checked={forProduceOnly}
            onCheckboxToggle={toggleForProduceOnly}
          />
          <span>
            {`${t("course.calendar.forProduceOnly")} (${readyForProduceCount})`}
          </span>
        </label>
        <div className="cc-ride-produce-toolbar__actions">
          <span className="cc-ride-produce-selected-badge">
            {t("course.calendar.produceSelectedBadge", {
              count: selectedCount,
            })}
          </span>
          {alreadyProducedCount > 0 ? (
            <span className="cc-ride-produce-badge cc-ride-produce-badge--already-produced">
              {t("course.calendar.produceAlreadyProducedBadge", {
                count: alreadyProducedCount,
              })}
            </span>
          ) : null}
          {activeNotSelectedCount > 0 ? (
            <span className="cc-ride-produce-badge cc-ride-produce-badge--skipped">
              {t("course.calendar.produceActiveNotSelectedBadge", {
                count: activeNotSelectedCount,
              })}
            </span>
          ) : null}
          <FramerTooltip content={t("course.calendar.produceSelectAllReady")}>
            <button
              type="button"
              className="cc-ride-produce-toolbar__icon-btn"
              onClick={onSelectAllReady}
              disabled={!readyForProduceCount}
              aria-label={t("course.calendar.produceSelectAllReady")}
            >
              <SelectAll fontSize="small" />
            </button>
          </FramerTooltip>
          <FramerTooltip content={t("course.calendar.produceDeselectAllReady")}>
            <button
              type="button"
              className="cc-ride-produce-toolbar__icon-btn"
              onClick={onDeselectAllReady}
              disabled={!selectedCount}
              aria-label={t("course.calendar.produceDeselectAllReady")}
            >
              <Deselect fontSize="small" />
            </button>
          </FramerTooltip>
        </div>
      </div>
      <div className="cc-ride-produce-table-wrap">
        <table className="cc-ride-produce-table">
          <thead>
            <tr>
              <th className="cc-ride-produce-col--check" aria-label="select" />
              <th>{t("course.calendar.producePreviewColCourse")}</th>
              <th>{t("course.calendar.producePreviewColDate")}</th>
              <th>{t("course.calendar.producePreviewColTime")}</th>
              <th>{t("course.calendar.producePreviewColDir")}</th>
              <CoursesCalendarRideProduceTableSeatsHead />
              <th>{t("course.calendar.producePreviewColRide")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleSlots.map(slot => (
              <CoursesCalendarRideProduceTableRow
                key={slot.id}
                slot={slot}
                isSelected={selectedSlotIds.has(slot.id)}
                onToggleSelected={onToggleSelected}
              />
            ))}
          </tbody>
        </table>
        {!visibleSlots.length ? (
          <div className="cc-ride-produce-empty">{t("general.NO_RESULTS")}</div>
        ) : null}
      </div>
    </>
  );
};
